/**
 * File: apps/qbench-cp/reactapp/src/shared/hooks/api/projects.js
 *
 * Purpose: React Query hook for creating a project.
 *
 * Scope: Project creation, including invalidating cached project list/options queries.
 *
 * Tags: customer-portal, trials
 *
 * Overview: `useProjectCreate` is a mutation that posts a new project and, on success,
 *     invalidates the cached project list and options queries so dependent views (e.g.
 *     project select dropdowns) refetch the updated set.
 *
 * Exports: useProjectCreate
 */

import { useMutation } from 'react-query';
import { QueryKeys } from 'shared/constants/entityTypes';
import { Projects } from 'shared/utils/api';
import { queryClient } from 'shared/utils/queryClient';

const entityName = QueryKeys.PROJECTS;

export function useProjectCreate() {
  const keysToInvalidate = [
    [entityName, 'list'],
    [entityName, 'options'],
  ];
  const mutationFn = (project) =>
    Projects.create(project).then((res) => res?.data);
  const { mutateAsync, ...mutationObject } = useMutation({
    mutationFn,
    onSuccess: () => {
      keysToInvalidate.forEach((k) => queryClient.invalidateQueries(k));
    },
  });

  return [mutateAsync, mutationObject];
}
